import {Navigate} from 'react-router'
import {fromJust} from '@freckle/maybe'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {
  useAsyncMap,
  useAsync,
  useAsyncOnceDefined
} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {useCourseMemberships} from '@freckle/educator-entities/ts/common/hooks/use-course-memberships'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import {groupByQuestionId} from '@freckle/educator-entities/ts/common/logic/school-assessment-report'
import type {GenericAssignmentSessionAttrsT} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import {fetchSchoolGenericAssignmentSessions} from '@freckle/educator-entities/ts/common/models/generic-assignment-session'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {
  fetchRlDomains,
  fetchRlStandardsWithDomain
} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {
  fetchRlStandardSetForSchool,
  fetchRlStandardSets
} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {AssessmentAnswerT} from '@freckle/educator-entities/ts/math/assessment/models/assessment-answer'
import {fetchAssessmentAnswers} from '@freckle/educator-entities/ts/math/assessment/models/assessment-answer'
import {
  fetchMathQuestionById,
  toQuestionMap
} from '@freckle/educator-entities/ts/math/common/models/questions'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import type {CourseMembershipAttrs} from '@freckle/educator-entities/ts/roster/models/course-membership'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {
  WithResourcesArray,
  WithResources
} from '@freckle/school/ts/common/components/with-resources'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {useNumFromUrlParamKey, useQueryParamAsNum} from '@freckle/school/ts/hooks/use-params'
import {useStudents} from '@freckle/school/ts/hooks/use-students'
import AssessmentReport from '@freckle/school/ts/pages/assessment-report/components/assessment-report'

export default function AssessmentReportHandler() {
  const schoolId = useNumFromUrlParamKey('pathSchoolId')
  const assessmentId = useNumFromUrlParamKey('pathAssessmentId')
  const teacherId = useQueryParamAsNum('teacher')

  const courses = useCourses()
  const courseMemberships = useCourseMemberships()

  if (schoolId === null || assessmentId === null || teacherId === null) {
    return <Navigate to={Routes.dashboard()} replace />
  }

  return (
    <WithResources
      resources={{courses, courseMemberships}}
      render={({courses, courseMemberships}) => (
        <Inner
          schoolId={schoolId}
          assessmentId={assessmentId}
          teacherId={teacherId}
          courses={courses}
          courseMemberships={courseMemberships}
        />
      )}
    />
  )
}

type InnerPropsT = {
  schoolId: number
  assessmentId: number
  teacherId: number
  courses: CourseAttrs[]
  courseMemberships: CourseMembershipAttrs[]
}

function Inner(props: InnerPropsT) {
  const {schoolId} = props
  const schoolStandardSet = useAsync(
    () => fetchRlStandardSetForSchool(schoolId),
    'fetchRlStandardSetForSchool',
    [schoolId]
  )
  const mathStandardSet = useAsyncOnceDefined(
    ({schoolStandardSet}) =>
      fetchRlStandardSets({'content-area': 'math', 'standard-set': schoolStandardSet.id}).then(
        standardSets => standardSets[0] ?? null
      ),
    'fetchRlStandardSet',
    ['math', schoolStandardSet.data],
    {schoolStandardSet}
  )
  return (
    <WithResources
      resources={{mathStandardSet}}
      render={({mathStandardSet}) => {
        if (mathStandardSet === null) {
          return <Navigate to={Routes.dashboard()} replace />
        }
        return <InnerMore mathStandardSet={mathStandardSet} {...props} />
      }}
    />
  )
}

type InnerMorePropsT = {
  schoolId: number
  assessmentId: number
  teacherId: number
  courses: CourseAttrs[]
  courseMemberships: CourseMembershipAttrs[]
  mathStandardSet: RlStandardSetT
}

function InnerMore(props: InnerMorePropsT) {
  const {schoolId, assessmentId, mathStandardSet} = props
  const students = useStudents()
  const teachers = useTeachersAsASchoolAdmin()
  const rlDomains = useAsync(
    () => fetchRlDomains('math', mathStandardSet, Languages.En),
    'fetchRlDomains',
    ['math', mathStandardSet, Languages.En]
  )
  const rlStandards = useAsync(
    () => fetchRlStandardsWithDomain('math', mathStandardSet.id, Languages.En),
    'fetchRlStandardsWithDomain',
    ['math', mathStandardSet.id, Languages.En]
  )
  const answers = useAsync(
    () =>
      fetchAssessmentAnswers({
        tag: 'school',
        id: schoolId,
        assignmentId: assessmentId
      }),
    'fetchAssessmentAnswers',
    [
      {
        tag: 'school',
        id: schoolId,
        assignmentId: assessmentId
      }
    ]
  )
  const sessions = useAsync(
    () =>
      fetchSchoolGenericAssignmentSessions(schoolId, [Products.MathAssessments], {
        mathStandardSetId: mathStandardSet.id,
        //TODO: fetch and use ela standard set when the route is available
        elaStandardSetId: null,
        assignmentIds: [assessmentId]
      }),
    'fetchSchoolGenericAssignmentSessions',
    [
      schoolId,
      [Products.MathAssessments],
      {
        mathStandardSetId: mathStandardSet.id,
        elaStandardSetId: null,
        assignmentIds: [assessmentId]
      }
    ]
  )

  return (
    <WithResources
      resources={{students, teachers, rlDomains, rlStandards, answers, sessions}}
      render={({students, teachers, rlDomains, rlStandards, answers, sessions}) => (
        <InnerMoreMore
          {...props}
          students={students}
          teachers={teachers}
          rlDomains={rlDomains}
          rlStandards={rlStandards}
          answers={answers}
          sessions={sessions}
        />
      )}
    />
  )
}

type InnerMoreMorePropsT = {
  teacherId: number
  courses: CourseAttrs[]
  courseMemberships: CourseMembershipAttrs[]
  mathStandardSet: RlStandardSetT
  sessions: GenericAssignmentSessionAttrsT[]
  answers: AssessmentAnswerT[]
  students: StudentAttrs[]
  teachers: BaseTeacherAttrs[]
  rlDomains: RlDomainT[]
  rlStandards: RlStandardWithDomainT[]
}

function InnerMoreMore({
  teacherId,
  courses,
  courseMemberships,
  mathStandardSet,
  students,
  teachers,
  rlDomains,
  rlStandards,
  answers,
  sessions
}: InnerMoreMorePropsT) {
  const teacher = fromJust(
    teachers.find(t => t.id === teacherId),
    `Could not find teacher with id ${teacherId}`
  )
  // Filter the student list to only have students accessible by teacher
  const studentsIds = getStudentsForTeacher(teacherId, courses, courseMemberships)
  const studentsForTeacher = students.filter(student => studentsIds.includes(student.id))
  // Filter the session list to only have sessions of students accessible by teacher
  const sessionsForTeacher = sessions.filter(({studentId}) => studentsIds.includes(studentId))
  const sessionIds = sessionsForTeacher.map(({sessionId}) => sessionId)
  // Filter the answers list to only have answers of students accessible by teacher
  const answersForTeacher = answers.filter(answer => sessionIds.includes(answer.asmtSessId))
  const {questionIdOrder} = groupByQuestionId(answersForTeacher, rlStandards)

  const questions = useAsyncMap(
    questionIdOrder,
    id => fetchMathQuestionById(id, teacher.language),
    'fetchMathQuestionById',
    [teacher.language]
  )

  return (
    <WithResourcesArray
      resources={{questions}}
      render={({questions}) => (
        <AssessmentReport
          assessmentSessions={sessionsForTeacher}
          assessmentAnswers={answersForTeacher}
          students={studentsForTeacher}
          questionMap={toQuestionMap(questions)}
          teacher={teacher}
          mathStandardSet={mathStandardSet}
          domains={rlDomains}
          standards={rlStandards}
        />
      )}
    />
  )
}

function getStudentsForTeacher(
  teacherId: number,
  courses: Array<CourseAttrs>,
  cms: Array<CourseMembershipAttrs>
): Array<number> {
  const teacherCourseIds = courses.filter(course => course.teacherId === teacherId).map(c => c.id)
  const cmForTeacherCourses = cms.filter(cm => teacherCourseIds.includes(cm.courseId))
  return cmForTeacherCourses.map(cm => cm.studentId)
}
